/**
 * Handing a Blob the API returned to the browser.
 *
 * Both helpers exist for the same reason: our export and document routes are
 * **authenticated**, so none of them can be a plain `<a href>`. The file comes back
 * through `fetchServer`, and every caller then wrote the same handful of DOM lines — six
 * copies of it, differing only in whether they said `URL` or `window.URL`. The lines are
 * not hard, they are just easy to get subtly wrong: forget `revokeObjectURL` and the blob
 * is pinned in memory for the rest of the session.
 *
 * Neither helper fetches anything. Callers already know how to load their own blob (each
 * has its own API client, its own error message, its own spinner); what they were
 * duplicating is only what happens *after*.
 */

/**
 * Save a blob to the user's disk under `filename`.
 *
 * The anchor has to be in the document for the click to count in every browser, hence the
 * append/remove pair. The object URL is revoked straight away: the download has already
 * been handed to the browser by the time `click()` returns.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * How long a blob opened in a tab stays alive. Revoking immediately would break the tab
 * that is still loading it, and never revoking leaks it for the rest of the session; a
 * minute is long past the point where the new tab has taken its own reference.
 */
const TAB_BLOB_TTL_MS = 60_000;

/**
 * Open a blob in a new tab — for a document someone wants to *look at* (a PDF, an image)
 * rather than save. `noopener` because the opened tab has no business reaching back into
 * this one.
 */
export const openBlobInTab = (blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), TAB_BLOB_TTL_MS);
};
